import { z } from 'zod';

import {
  assertPickedDayCoherent,
  CivilDateDto,
  PickedCivilDayDto,
} from './civilDate';
import { isRealCivilDay, isWireDay } from '../utils/date';

import {
  ScheduleNoteNotifyRecepients,
  ScheduleNoteRecurrenceType,
  ScheduleNoteVisibility,
} from '../enums';

export const CreateScheduleNoteDto = z
  .object({
    title: z.string().min(1).max(255),
    description: z.string().optional(),
    dateTime: z.string().datetime(),
    // D1: the civil day the scheduler PICKED (global-timezone spec)
    businessDate: PickedCivilDayDto.optional(),
    clientVisibility: z.nativeEnum(ScheduleNoteVisibility),
    sendNotification: z.boolean(),
    notifyRecipients: z.nativeEnum(ScheduleNoteNotifyRecepients).optional(),
    recurrenceType: z.nativeEnum(ScheduleNoteRecurrenceType).optional(),
    recurrenceInterval: z.number().int().positive().optional(),
    recurrenceEndDate: CivilDateDto.optional(),
    // Dual-mode entries, same rule as the questionnaire twin: a bare civil
    // day is the picked day verbatim; ISO instants stay for legacy bundles.
    customDates: z
      .array(
        z
          .string()
          .refine(
            (value) =>
              isRealCivilDay(value) || !Number.isNaN(Date.parse(value)),
            { message: 'VALIDATION__InvalidDate' },
          ),
      )
      .optional(),
    clientId: z.string().uuid(),
    organizationId: z.string().uuid().optional(),
  })
  .refine(
    (data) => !data.sendNotification || data.notifyRecipients !== undefined,
    {
      message: 'VALIDATION__NotifyRecipientsRequired',
      path: ['notifyRecipients'],
    },
  )
  // EVERY_N_DAYS without an end day expands to a SINGLE occurrence — a
  // payload claiming the type without the bound is always a client bug.
  .refine(
    (data) =>
      data.recurrenceType !== ScheduleNoteRecurrenceType.EVERY_N_DAYS ||
      data.recurrenceEndDate !== undefined,
    { message: 'VALIDATION__Required', path: ['recurrenceEndDate'] },
  )
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );

export type ApiCreateScheduleNoteDto = z.infer<typeof CreateScheduleNoteDto>;

export const UpdateScheduleNoteDto = z
  .object({
    clientVisibility: z.nativeEnum(ScheduleNoteVisibility).optional(),
    notifyRecipients: z.nativeEnum(ScheduleNoteNotifyRecepients).optional(),
    sendNotification: z.boolean().optional(),
    description: z.string().optional(),
    dateTime: z.string().datetime().optional(),
    // D1: the civil day the scheduler PICKED (global-timezone spec)
    businessDate: PickedCivilDayDto.optional(),
    title: z.string().min(1).max(255).optional(),
  })
  .superRefine((data, ctx) =>
    assertPickedDayCoherent(ctx, data.businessDate, data.dateTime),
  );

export type ApiUpdateScheduleNoteDto = z.infer<typeof UpdateScheduleNoteDto>;

export const ListScheduleNotesQueryDto = z.object({
  // Optional: omitting it against the organizations/:organizationId mount
  // requests the org-wide (OWNER-only) listing instead of one client's rows.
  // The bare mount still refuses a request without it, in the handler.
  clientId: z.string().uuid().optional(),
  // Day-anchored scheduling: a bare `yyyy-MM-dd` pair filters the frozen
  // `businessDate` column; ISO instants keep the legacy dateTime window.
  from: z.string().refine(isWireDay, {
    message: 'VALIDATION__InvalidDayOrDateTime',
  }),
  to: z.string().refine(isWireDay, {
    message: 'VALIDATION__InvalidDayOrDateTime',
  }),
});

export type ApiListScheduleNotesQueryDto = z.infer<
  typeof ListScheduleNotesQueryDto
>;

export const ListScheduleNotesHistoryQueryDto = z.object({
  clientId: z.string().uuid(),
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});

export type ApiListScheduleNotesHistoryQueryDto = z.infer<
  typeof ListScheduleNotesHistoryQueryDto
>;
